import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import PATH from '@/config/paths/PATH';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getThemeManagement } from '@/services/theme-management';
import { Space, Tag } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const statusConfig = {
	ACTIVE: {
		label: "ACTIVE",
		color: "#16a34a",
		bg: "#f0fdf4",
		border: "#86efac",
	},
	SCHEDULED: {
		label: "SCHEDULED",
		color: "#2563eb",
		bg: "#eff6ff",
		border: "#93c5fd",
	},
	EXPIRED: {
		label: "EXPIRED",
		color: "#9ca3af",
		bg: "#f9fafb",
		border: "#d1d5db",
	},
};

const filterOptions = [
	{
		label: 'All',
		value: null,
	},
	{
		label: 'Active',
		value: 'ACTIVE',
	},
	{
		label: 'Expired',
		value: 'EXPIRED',
	},
	{
		label: 'Scheduled',
		value: 'SCHEDULED',
	},
];

const StatusBadge = ({ status }) => {
	const cfg = statusConfig[status] || null;
	if (!cfg) return null;
	return (
		<Tag
			style={{
				color: cfg.color,
				background: cfg.bg,
				borderColor: cfg.border,
				borderRadius: 6,
				fontWeight: 600,
				padding: "2px 12px",
				fontSize: 13,
			}}
		>
			{cfg.label}
		</Tag>
	);
}

export default function ThemeManagement() {
	const navigate = useNavigate();
	const [searchValue, setSearchValue] = useState(null);
	const [selectedFilter, setSelectedFilter] = useState(null);
	const [time, setTime] = useState(null);
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(1);
	const offset = (pageIndex - 1) * pageSize + 1;
	const [queryValues, setQueryValues] = useState({
		keyword: null,
		status: null,
		time: null,
	});

	const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
		() =>
			getThemeManagement({
				...queryValues,
				page: pageIndex,
				size: pageSize,
			}),
		['themes-management', queryValues, pageIndex, pageSize]
	);

	const handleQuery = () => {
		setPageIndex(1);
		setQueryValues({
			keyword: searchValue?.trim(),
			status: selectedFilter || null,
			time,
		});
	};

	const handleReset = () => {
		setPageIndex(1);
		setSearchValue(null);
		setSelectedFilter(null);
		setTime(null);
		setQueryValues({
			keyword: null,
			status: null,
			time: null,
		});
	};

	const handleRefresh = () => {
		refetch();
	};

	const handleOnEdit = (record) => {
		navigate(PATH.THEME_MANAGEMENT.UPDATE.replace(':id', record?.id));
	};

	const handleOnCreate = () => navigate(PATH.THEME_MANAGEMENT.CREATE);

	const columns = [
		{
			title: 'No.',
			dataIndex: 'id',
			key: 'id',
			render: (_, __, index) => offset + index,
		},
		{
			title: 'Theme Name',
			dataIndex: 'themeName',
			key: 'themeName',
			render: (value) => (value ? value : '-'),
		},
		{
			title: 'Type',
			dataIndex: 'themeType',
			key: 'themeType',
			render: (value) => (value ? value : '-'),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusBadge status={status} />,
		},
		{
			title: 'Period',
			dataIndex: 'period',
			key: 'period',
			render: (_, record) => {
				if (!record?.startTime || !record?.endTime) return '-';
				return `${dayjs(record.startTime).format('DD/MM/YYYY')} - ${dayjs(record.endTime).format('DD/MM/YYYY')}`;
			},
		},
	];

	return (
		<Space direction="vertical" className="w-full">
			<AppPageTitle title="Theme Management" />
			<AppTableQuery
				filterOptions={filterOptions}
				filterPlaceholder="Active status"
				filterLabel="Status"
				selectedFilter={selectedFilter}
				setSelectedFilter={setSelectedFilter}
				searchValue={searchValue}
				searchPlaceholder="Theme Name"
				setSearchValue={setSearchValue}
				handleQuery={handleQuery}
				handleReset={handleReset}
				time={time}
				setTime={setTime}
				hasDatePicker
				timePlaceholder="Time"
				canDisableFutureDate={false}
			/>
			<AppTable
				columns={columns}
				dataSource={data?.data || []}
				loading={isLoading || isRefetching}
				totalItems={data?.pagination?.total || 0}
				pageIndex={pageIndex}
				pageSize={pageSize}
				onPageChange={(page, size) => {
					setPageIndex(page);
				}}
				onPageSizeChange={(newSize) => {
					setPageSize(newSize);
				}}
				onCreate={handleOnCreate}
				onRefresh={handleRefresh}
				hasRowAction
				onEditAction={handleOnEdit}
			/>
		</Space>
	)
}
